/**
 * PDF viewer.
 *
 * pdf.js renders; the handler owns structure and annotations. The two never
 * disagree because they read the same bytes and neither writes to the other's
 * model.
 *
 * Pages render lazily through an `IntersectionObserver`. §11 requires the first
 * page to appear before the rest is ready, and a hundred-page document rendered
 * eagerly would block the tab for several seconds — so a page becomes a canvas
 * only when it approaches the viewport, and releases it when it leaves.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PageViewport, PDFDocumentProxy } from 'pdfjs-dist';
import type { PdfAnnotation } from '@moduviewer/handlers';
import { AnnotationLayer } from './pdf-annotations.js';
import type { AnnotationTool } from './pdf-annotations.js';
import type { RgbColor } from '@moduviewer/handlers';
import * as pdfjs from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

export interface PdfViewerProps {
  readonly bytes: Uint8Array;
  readonly zoom: number;
  readonly onPageVisible?: ((page: number) => void) | undefined;
  /**
   * Annotation state, present only while editing.
   *
   * Absent means the viewer renders and nothing else — no text layer, no
   * overlay, no pointer handlers. A reader who is not annotating should not be
   * paying for the machinery of annotating (§16, §33).
   */
  readonly annotating?: {
    readonly annotations: readonly PdfAnnotation[];
    readonly tool: AnnotationTool;
    readonly color: RgbColor;
    readonly onAdd: (annotation: PdfAnnotation) => void;
    readonly onRemove: (index: number) => void;
  } | undefined;
}

export function PdfViewer({
  bytes, zoom, onPageVisible, annotating,
}: PdfViewerProps): React.JSX.Element {
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    // A copy, because pdf.js takes ownership of the buffer it is handed and the
    // handler still needs the original for saving.
    const task = pdfjs.getDocument({
      data: bytes.slice(0),
      // §30 — the document must not fetch anything of its own. XFA is a
      // scripting surface and stays off.
      disableAutoFetch: true,
      enableXfa: false,
      // A single pathological image (a poster-resolution scan inside a
      // brochure) decoded at full size can exhaust a phone's renderer memory
      // and take every page down with it — the whole document goes white. Cap
      // the pixels pdf.js will decode per image; anything larger is drawn at a
      // reduced size instead of blowing up the tab.
      maxImageSize: 24_000_000,
    });

    task.promise.then(
      (d) => { if (!cancelled) setDoc(d); },
      (err: Error) => { if (!cancelled) setError(err.message); },
    );

    // Destroying the loading task tears down the document and its worker port;
    // there is nothing else to release.
    return () => { cancelled = true; void task.destroy(); };
  }, [bytes]);

  if (error) {
    return (
      <div className="loading" role="alert">
        <span>이 PDF를 표시할 수 없습니다.</span>
        <span className="mono" style={{ color: 'var(--text-tertiary)' }}>{error}</span>
      </div>
    );
  }
  if (!doc) {
    return (
      <div className="loading">
        <div className="loading__bar" />
        <span>안전한 미리보기를 준비하고 있습니다</span>
      </div>
    );
  }

  return (
    <div className="canvas">
      <div className="pdf">
        {Array.from({ length: doc.numPages }, (_, i) => (
          <PdfPage
            key={i}
            doc={doc}
            index={i}
            zoom={zoom}
            onVisible={onPageVisible}
            annotating={annotating}
          />
        ))}
      </div>
    </div>
  );
}

function PdfPage(
  { doc, index, zoom, onVisible, annotating }: {
    doc: PDFDocumentProxy; index: number; zoom: number;
    onVisible?: ((page: number) => void) | undefined;
    annotating?: PdfViewerProps['annotating'];
  },
): React.JSX.Element {
  const host = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const textHost = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<PageViewport | null>(null);
  const [near, setNear] = useState(index === 0);
  const [size, setSize] = useState({ width: 595 * zoom, height: 842 * zoom });
  const [rendered, setRendered] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  // Devices vary; rendering at CSS pixels on a 2× display produces visibly soft
  // text, which is the first thing anyone notices in a document viewer.
  const dpr = useMemo(() => Math.min(window.devicePixelRatio || 1, 2), []);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setNear(true);
            if (entry.intersectionRatio > 0.5) onVisible?.(index);
          }
        }
      },
      { root: null, rootMargin: '600px 0px', threshold: [0, 0.5] },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [index, onVisible]);

  useEffect(() => {
    if (!near) return;
    let cancelled = false;
    let task: { cancel(): void } | null = null;
    setPageError(null);

    void (async () => {
      let page;
      try {
        page = await doc.getPage(index + 1);
      } catch (err) {
        if (!cancelled) setPageError(err instanceof Error ? err.message : String(err));
        return;
      }
      if (cancelled) return;
      const viewport = page.getViewport({ scale: zoom });
      setSize({ width: viewport.width, height: viewport.height });
      setViewport(viewport);

      const el = canvas.current;
      if (!el) return;
      el.style.width = `${viewport.width}px`;
      el.style.height = `${viewport.height}px`;

      // Cap the canvas backing store. Mobile WebViews (KakaoTalk's in-app
      // browser, Android System WebView, iOS Safari) refuse to paint a canvas
      // past a memory/area limit and hand back a blank white bitmap with no
      // error — this is exactly the "PDF opened but every page is white"
      // failure. Touch devices get a lower pixel ratio and a smaller area cap,
      // and if a render still fails (out of memory on a huge page) the scale is
      // halved and retried rather than leaving the reader a white page. Text
      // stays crisp; only the very largest pages lose a little sharpness.
      const coarse = window.matchMedia('(pointer: coarse)').matches;
      const baseDpr = coarse ? 1 : dpr;
      const maxArea = coarse ? 2_000_000 : 4_000_000;
      const wanted = viewport.width * viewport.height * baseDpr * baseDpr;
      let scale = wanted > maxArea ? baseDpr * Math.sqrt(maxArea / wanted) : baseDpr;

      for (let attempt = 0; attempt < 3 && !cancelled; attempt++) {
        el.width = Math.max(1, Math.floor(viewport.width * scale));
        el.height = Math.max(1, Math.floor(viewport.height * scale));
        const context = el.getContext('2d');
        if (!context) { if (!cancelled) setPageError('캔버스를 초기화할 수 없습니다.'); return; }
        context.setTransform(scale, 0, 0, scale, 0, 0);

        const render = page.render({ canvasContext: context, viewport, canvas: el });
        task = render;
        try {
          await render.promise;
          if (!cancelled) { setRendered(true); setPageError(null); }
          return;
        } catch (err) {
          // A newer render (zoom change / unmount) cancelled this one — leave.
          if (cancelled) return;
          // Otherwise the device could not paint at this size: shrink and retry.
          scale = scale / 2;
          if (attempt === 2) {
            setPageError(err instanceof Error ? err.message : '이 페이지를 표시할 수 없습니다.');
          }
        }
      }
    })();

    return () => { cancelled = true; task?.cancel(); };
  }, [doc, index, zoom, near, dpr]);

  /*
   * The text layer.
   *
   * Rendered as invisible, selectable spans positioned over the canvas — the
   * standard pdf.js arrangement, and what makes a highlight follow the words a
   * person selected rather than a rectangle they dragged.
   *
   * Only while annotating. It is one DOM node per text run, which on a
   * hundred-page document is tens of thousands of nodes that a reader who is
   * only reading never needs (§33).
   */
  useEffect(() => {
    if (!near || !annotating) return;
    const el = textHost.current;
    if (!el) return;

    let cancelled = false;
    let layer: { cancel(): void } | null = null;

    void (async () => {
      const page = await doc.getPage(index + 1);
      if (cancelled) return;
      const viewport = page.getViewport({ scale: zoom });
      const textContent = await page.getTextContent();
      if (cancelled) return;

      el.replaceChildren();
      pdfjs.setLayerDimensions(el, viewport);
      const text = new pdfjs.TextLayer({ textContentSource: textContent, container: el, viewport });
      layer = text;
      try {
        await text.render();
      } catch { /* superseded by a newer render */ }
    })();

    return () => { cancelled = true; layer?.cancel(); };
  }, [doc, index, zoom, near, annotating]);

  return (
    <div
      className="pdf__page"
      ref={host}
      style={{ width: size.width, height: size.height }}
      data-loading={rendered ? undefined : 'true'}
      aria-label={`${index + 1}페이지`}
    >
      {near ? <canvas ref={canvas} /> : null}

      {/* A silent white page is the worst outcome: the reader cannot tell the
          document from a rendering failure. If the device could not paint the
          page, say so plainly instead of leaving it blank. */}
      {pageError ? (
        <div className="pdf__page-error" role="alert">
          <span>이 페이지를 표시할 수 없습니다</span>
          <span className="mono">{pageError}</span>
        </div>
      ) : null}

      {/* The text layer exists only while annotating: it is what makes a
          highlight follow the words rather than a rectangle the user dragged,
          and it costs a DOM node per span on every page. */}
      {near && annotating ? <div className="pdf__text" ref={textHost} /> : null}

      {near && annotating && viewport ? (
        <AnnotationLayer
          pageIndex={index}
          viewport={viewport}
          annotations={annotating.annotations.filter((a) => a.page === index)}
          indices={annotating.annotations
            .map((a, i) => (a.page === index ? i : -1))
            .filter((i) => i >= 0)}
          tool={annotating.tool}
          color={annotating.color}
          onAdd={annotating.onAdd}
          onRemove={annotating.onRemove}
        />
      ) : null}

      <span className="pdf__pagenum">{index + 1}</span>
    </div>
  );
}
